import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { javniUpitApi } from '../api/ponude'
import type { JavniUpit } from '../api/types'
import { IzborTermina } from '../components/IzborTermina'
import { Ekran, Greska, Gumb, Pilula, Ucitavanje, Unos } from '../components/noir'
import { porukaGreske } from '../i18n/greske'
import { datumOznaka, poljaIzTrenutka, trenutakIzPolja } from '../lib/termin'

/** Tri gumba iz poruke; ista imena koja nosi i putanja poslužitelja. */
type Akcija = 'potvrdi' | 'odbij' | 'protuprijedlog'

const AKCIJE: Akcija[] = ['potvrdi', 'odbij', 'protuprijedlog']

/**
 * JAVNA STRANICA IZ E-POŠTE (Z22c §A): pružatelj koji Shuttly nema odgovara na
 * upit jednim tapom.
 *
 * **MINIMALNA i BEZ PRIJAVE, i to je cijela poanta.** Konoba u zaleđu nema naš
 * račun i nikad ga neće imati; ono što ima je adresa e-pošte i mobitel u ruci.
 * Zato ovdje nema ni ljuske, ni pojasa, ni izbornika — jedan ekran, jedan
 * odgovor, i gotovo.
 *
 * **Dokaz je TOKEN iz adrese** (`?t=`), i drugog nema: stranica ne diže ni
 * gostovu ni sesiju osoblja (`javniUpitApi` ide golim `raw`-om). Akcija iz
 * adrese (`?a=`) samo OTVARA odgovarajući korak — ne šalje ništa sama, jer
 * poruka u sandučiću ne smije potvrditi rezervaciju time što ju je netko
 * pregledao (mail klijenti prefetchaju veze).
 *
 * **Potrošen token nije zid nego odgovor**: tko je već odgovorio, vidi ŠTO je
 * odgovorio (`rijesen`). Nepoznat i istekao izgledaju jednako i ne kažu ništa —
 * anonimna površina daje uvijek isti odgovor (§17).
 */
export function UpitOdgovor() {
  const { t, i18n } = useTranslation()
  const jezik = i18n.resolvedLanguage ?? 'en'
  const [parametri] = useSearchParams()

  const token = parametri.get('t')?.trim() ?? ''
  const trazena = parametri.get('a')?.trim() ?? ''

  const [upit, postaviUpit] = useState<JavniUpit | null>(null)
  const [ucitava, postaviUcitava] = useState(true)
  const [greska, postaviGresku] = useState<unknown>(null)
  const [salje, postaviSalje] = useState(false)

  // Korak koji je poruka otvorila. Predložak je samo PRIJEDLOG ekrana: potvrda
  // i odbijanje su jedan tap, a protuprijedlog traži datum i vrijeme.
  const [obrazac, postaviObrazac] = useState<Akcija | null>(
    AKCIJE.includes(trazena as Akcija) ? (trazena as Akcija) : null,
  )

  /*
    OBRAZAC KREĆE OD GOSTOVOG TERMINA (dotjerivanje-4 §2), kao i u redu upita:
    protuprijedlog je POMAK onoga što je gost tražio, a ne novi upis.

    Prijedlog se računa PRI CRTANJU, a ne u efektu (isto pravilo kao gostov
    obrazac): upit stiže nakon prvog kadra, pa bi efekt značio jedan kadar s
    praznim poljima i utrku koja se vidi tek kad je netko brz. `null` znači "nije
    dirao" — čim pružatelj polje dotakne, vrijedi njegova vrijednost.
  */
  const [upisanDatum, postaviDatum] = useState<string | null>(null)
  const [upisanoVrijeme, postaviVrijeme] = useState<string | null>(null)
  const [poruka, postaviPoruku] = useState('')

  const gostov = poljaIzTrenutka(upit?.pocetak_utc)
  const datum = upisanDatum ?? gostov.datum
  const vrijeme = upisanoVrijeme ?? gostov.vrijeme

  const predlozen = trenutakIzPolja(datum, vrijeme)

  useEffect(() => {
    let ziv = true

    if (!token) {
      postaviUcitava(false)
      return
    }

    javniUpitApi
      .detalj(token)
      .then((zapis) => {
        if (ziv) postaviUpit(zapis)
      })
      .catch((kvar: unknown) => {
        if (ziv) postaviGresku(kvar)
      })
      .finally(() => {
        if (ziv) postaviUcitava(false)
      })

    return () => {
      ziv = false
    }
  }, [token])

  async function odgovori(akcija: Akcija) {
    if (salje) return

    postaviSalje(true)
    postaviGresku(null)

    try {
      postaviUpit(
        await javniUpitApi.odgovori(
          token,
          akcija,
          akcija === 'protuprijedlog'
            ? { pocetak_utc: predlozen, poruka: poruka.trim() || null }
            : { poruka: poruka.trim() || null },
        ),
      )

      postaviObrazac(null)
    } catch (kvar) {
      postaviGresku(kvar)
    } finally {
      postaviSalje(false)
    }
  }

  return (
    <div className="min-h-dvh bg-noir">
      <Ekran>
        <header className="mb-5">
          <p className="text-[11px] tracking-[0.22em] text-brass uppercase">Shuttly</p>
          <h1 className="font-serif text-2xl text-champagne">{t('javniUpit.naslov')}</h1>
        </header>

        {ucitava ? <Ucitavanje /> : null}

        {greska ? <Greska poruka={porukaGreske(t, greska)} /> : null}

        {upit ? (
          <>
            <div className="mb-4 rounded-xl border border-line bg-surface px-4 py-3">
              <p className="text-[13px] text-stone">{upit.resort}</p>
              <p className="text-[17px] text-ivory">{upit.ponuda}</p>

              <p className="mt-2 font-serif text-xl text-champagne">
                {datumOznaka(upit.pocetak_utc, jezik)}
              </p>

              <p className="text-[13px] text-stone">
                {[upit.gost_ime, t('javniUpit.osoba', { broj: upit.broj_osoba })].join(' · ')}
              </p>

              {upit.napomena ? (
                <p className="mt-2 text-[15px] text-ivory" data-napomena="gosta">
                  {upit.napomena}
                </p>
              ) : null}
            </div>

            {upit.rijesen ? (
              <div data-rijesen="da">
                <Pilula ton="mirno">{t(`gost.rezervacije.status.${upit.status}`)}</Pilula>

                <p className="mt-2 text-[15px] text-stone">
                  {upit.predlozeni_pocetak_utc
                    ? t('javniUpit.predlozeno', {
                        kad: datumOznaka(upit.predlozeni_pocetak_utc, jezik),
                      })
                    : t('javniUpit.rijesen')}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2" data-odluka="upit">
                {obrazac === 'protuprijedlog' ? (
                  <>
                    <IzborTermina
                      datum={datum}
                      vrijeme={vrijeme}
                      naDatum={postaviDatum}
                      naVrijeme={postaviVrijeme}
                    />
                    <Unos
                      oznaka={t('osoblje.upiti.poruka')}
                      maxLength={1000}
                      value={poruka}
                      onChange={(dogadaj) => postaviPoruku(dogadaj.target.value)}
                    />

                    <Gumb
                      disabled={salje || !predlozen}
                      onClick={() => void odgovori('protuprijedlog')}
                      data-potvrdi="predlog"
                    >
                      {t('osoblje.upiti.posaljiPredlog')}
                    </Gumb>

                    <Gumb izgled="prigusen" onClick={() => postaviObrazac(null)}>
                      {t('common.cancel')}
                    </Gumb>
                  </>
                ) : (
                  <>
                    <Gumb
                      disabled={salje}
                      onClick={() => void odgovori('potvrdi')}
                      data-akcija="potvrdi"
                    >
                      {t('osoblje.upiti.potvrdi')}
                    </Gumb>

                    <Gumb
                      izgled="prigusen"
                      onClick={() => postaviObrazac('protuprijedlog')}
                      data-akcija="predlog"
                    >
                      {t('osoblje.upiti.predlozi')}
                    </Gumb>

                    <Gumb
                      izgled="prigusen"
                      disabled={salje}
                      onClick={() => void odgovori('odbij')}
                      data-akcija="odbij"
                    >
                      {t('osoblje.upiti.odbij')}
                    </Gumb>
                  </>
                )}
              </div>
            )}
          </>
        ) : null}

        {!ucitava && !upit && !greska ? (
          <Greska poruka={t('errors.upit_token_invalid')} />
        ) : null}
      </Ekran>
    </div>
  )
}
